import moment from 'moment';
// Import and re-export centralized date utilities
import { 
  formatDateToMMDD as formatDateToMMDDUtil,
  stripTimeFromDates as stripTimeFromDatesUtil
} from '../../../utils/dateUtils';

// Re-export centralized date utilities to maintain compatibility
export const formatDateToMMDD = formatDateToMMDDUtil;
export const stripTimeFromDates = stripTimeFromDatesUtil;

// Currency formatting utility
export const formatCurrency = (value) => {
  if (typeof value === "number") {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  } else if (typeof value === "string" && value.trim() !== "") {
    // Convert string to number first
    const numValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
    if (!isNaN(numValue)) {
      return numValue.toLocaleString("en-US", {
        style: "currency", 
        currency: "USD",
      });
    }
  }
  return value || "N/A";
};



// Format all jobs dates
export const formatJobDates = (jobs) => {
  return jobs.map((job) => stripTimeFromDates(job));
};

// Group jobs by ship date
export const groupJobsByShipDate = (jobs, weekColumns = []) => {
  // Initialize with "Past Due" and "Unscheduled" groups
  const result = {
    "Past Due": [],
    "Unscheduled": []
  };
  
  // Current date for checking past due items
  const today = moment().startOf('day');
  // Determine the current week's start date
  let currentWeekStart = null;
  
  if (weekColumns && weekColumns.length > 0) {
    // Find which week contains today's date
    for (let i = 0; i < weekColumns.length; i++) {
      const weekStart = moment(weekColumns[i].startDate).startOf('day');
      const weekEnd = moment(weekColumns[i].endDate).endOf('day');
      if (today.isBetween(weekStart, weekEnd, null, '[]')) { // [] means inclusive
        currentWeekStart = weekStart;
        break;
      }
    }
  }
  
  // Group jobs by whether they have a ship date
  jobs.forEach(job => {
    // Only consider unshipped jobs (ActualShipDate is null)
    if (job.ActualShipDate) return;
    
    // Check if job has a CurrentPlanShipDate
    if (job.CurrentPlanShipDate) {
      // Use moment to create the date to avoid timezone issues
      const shipDate = moment(job.CurrentPlanShipDate).startOf('day');
      
      // Check if the ship date is before the current week's start date
      if (currentWeekStart && shipDate.isBefore(currentWeekStart)) {
        result["Past Due"].push(job);
        return;
      }
      
      // Find which week this ship date belongs to
      let weekFound = false;
      
      for (let i = 0; i < weekColumns.length; i++) {
        const weekStart = moment(weekColumns[i].startDate).startOf('day');
        const weekEnd = moment(weekColumns[i].endDate).endOf('day');
        
        if (shipDate >= weekStart && shipDate <= weekEnd) {
          const weekLabel = `Week ${i + 1}`;
          
          if (!result[weekLabel]) {
            result[weekLabel] = [];
          }
          
          result[weekLabel].push(job);
          weekFound = true;
          break;
        }
      }
      
      if (!weekFound) {
        result["Unscheduled"].push(job);
      }
    } else {
      result["Unscheduled"].push(job);
    }
  });
    // Sort jobs within each week
  Object.keys(result).forEach(weekKey => {
    result[weekKey].sort((a, b) => {
      // For sections with ship dates, sort by CurrentPlanShipDate first, then by JobNumber
      if (weekKey !== "Unscheduled") {
        // First sort by CurrentPlanShipDate
        const dateA = a.CurrentPlanShipDate ? new Date(a.CurrentPlanShipDate) : null;
        const dateB = b.CurrentPlanShipDate ? new Date(b.CurrentPlanShipDate) : null;
        
        if (!dateA && !dateB) {
          // Both have no date, sort by JobNumber
          return (a.JobNumber || '').localeCompare(b.JobNumber || '');
        }
        if (!dateA) return 1; // Jobs without dates go to the end
        if (!dateB) return -1; // Jobs without dates go to the end
        
        const dateComparison = dateA - dateB;
        if (dateComparison !== 0) {
          return dateComparison; // Sort by date if different
        }
        
        // If dates are the same, sort by JobNumber
        return (a.JobNumber || '').localeCompare(b.JobNumber || '');
      } else {
        // For Unscheduled section, sort by JobNumber only
        if (!a.JobNumber && !b.JobNumber) return 0;
        if (!a.JobNumber) return 1;
        if (!b.JobNumber) return -1;
        return a.JobNumber.localeCompare(b.JobNumber);
      }
    });
  });
  
  return result;
};

// Excel export helper
export const formatValueForExcel = (field, value) => {
  const fieldsToStayBlank = [
    "TransportType", 
    "CurrentPlanShipDate", 
    "ActualShipDate", 
    "ScheduledArrivalDate", 
    "SalesRep",
    // Done fields - should show blank instead of N/A
    "MechanicalDone",
    "ElectricalDone",
    "ControlsDone",
    "EngineeringDone",
    "NPPipeFabDone",
    "FabDone",
    "SoftwareDone",
    "ControlPanelDone",
    "HCPumpsDone",
    "PumpDone",
    "PumpsDone", // Alternative name for PumpDone
    "AssemblyDone",
    "TestDone",
    "NPCSMDone",
    "NPSuctionLineDone",
    "NPInducerTubeDone",
    "NPBMXDone",
    "PackDone",
    "MechanicalLongLeadsDone",
    "ElectricalLongLeadsDone",
    // Expected Completion fields - should show blank instead of N/A
    "ControlPanelExpectedCompletion",
    "AssemblyExpectedCompletion",
    "TestExpectedCompletion",
    "FabExpectedCompletion",
    "PumpExpectedCompletion"
  ];
  
  if (value === undefined || value === null || value === "") {
    if (fieldsToStayBlank.includes(field)) {
      return "";
    }
    return "N/A";
  }
  
  return value;
};

// Add this new function after the existing utility functions
export const calculateLeadTime = (productionReleaseDate, currentPlanShipDate) => {
  if (!productionReleaseDate || !currentPlanShipDate) {
    return null;
  }

  const releaseDate = moment(productionReleaseDate);
  const shipDate = moment(currentPlanShipDate);
  
  // Calculate the difference in days
  const totalDays = shipDate.diff(releaseDate, 'days');
  
  if (totalDays < 0) {
    return { weeks: 0, days: 0, totalDays: 0, isNegative: true };
  }
  
  // Calculate weeks and remaining days
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  
  return { weeks, days, totalDays, isNegative: false };
};

export const formatLeadTime = (leadTime) => {
  if (!leadTime) return '-';
  
  if (leadTime.isNegative) {
    return 'Ship before release';
  }
  
  if (leadTime.totalDays === 0) {
    return 'Same day';
  }
  
  const parts = [];
  if (leadTime.weeks > 0) {
    parts.push(`${leadTime.weeks}w`);
  }
  if (leadTime.days > 0) {
    parts.push(`${leadTime.days}d`);
  }
  
  return parts.join(' ');
};

// Get initials from a name (handles various formats)
export const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '';
  
  // Trim whitespace
  const trimmedName = name.trim();
  if (!trimmedName) return '';
  
  // Check if it's already initials (all uppercase, 2-3 letters)
  if (/^[A-Z]{2,3}$/.test(trimmedName)) {
    return trimmedName;
  }
  
  // Split by spaces and filter out empty parts
  const parts = trimmedName.split(/\s+/).filter(part => part.length > 0);
  
  if (parts.length === 0) return '';
  
  // If single word
  if (parts.length === 1) {
    const word = parts[0];
    // If it's a single letter, return it uppercase
    if (word.length === 1) {
      return word.toUpperCase();
    }
    // Otherwise, return first two letters uppercase
    return word.substring(0, 2).toUpperCase();
  }
  
  // If two or more words, return first letter of first name and last letter of last name
  // This handles middle names/initials properly (e.g., "Will T. Shull" -> "WS", "Greg I. Salisbury" -> "GS")
  const firstNameInitial = parts[0][0];
  const lastNameInitial = parts[parts.length - 1][0]; // Get the last part as the last name
  
  return (firstNameInitial + lastNameInitial).toUpperCase();
};
